import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCents } from "@/lib/format";
import { cn } from "@/lib/utils";

type Size = "sm" | "md";

type Props = {
  totalCents: number;
  spentCents: number;
  pendingCents: number;
  currency: string;
  size?: Size;
  showLegend?: boolean;
  className?: string;
};

const trackHeight: Record<Size, string> = {
  sm: "h-1.5",
  md: "h-2.5",
};

export const CampaignBudgetBar = ({
  totalCents,
  spentCents,
  pendingCents,
  currency,
  size = "md",
  showLegend = false,
  className,
}: Props) => {
  const { t } = useTranslation();

  const total = Math.max(0, totalCents || 0);
  const spent = Math.max(0, spentCents || 0);
  const rawPending = Math.max(0, pendingCents || 0);

  // Visual clamp: pending can't push the bar beyond 100%.
  const visibleSpent = total > 0 ? Math.min(spent, total) : 0;
  const visiblePending = total > 0 ? Math.min(rawPending, Math.max(0, total - visibleSpent)) : 0;
  const visibleRemaining = Math.max(0, total - visibleSpent - visiblePending);

  const spentPct = total > 0 ? (visibleSpent / total) * 100 : 0;
  const pendingPct = total > 0 ? (visiblePending / total) * 100 : 0;
  const remainingPct = total > 0 ? (visibleRemaining / total) * 100 : 100;

  // Underlying numbers for the legend/tooltip — these are *not* clamped, so the user
  // sees the real amounts even if pending overshoots the visible track.
  const remainingForLegend = Math.max(0, total - spent);
  const tooltipText = t("campaign.budget.pendingTooltip");

  return (
    <div className={cn("w-full", className)}>
      <div className={cn("relative w-full rounded-full overflow-hidden bg-muted flex", trackHeight[size])}>
        {visibleSpent > 0 && (
          <div
            className="bg-primary h-full"
            style={{ width: `${spentPct}%` }}
            aria-label={t("campaign.budget.spent")}
          />
        )}
        {visiblePending > 0 && (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <div
                className="bg-amber-500/70 pending-stripes h-full cursor-help"
                style={{ width: `${pendingPct}%` }}
                aria-label={t("campaign.budget.pending")}
              />
            </TooltipTrigger>
            <TooltipContent side="top">{tooltipText}</TooltipContent>
          </Tooltip>
        )}
        {visibleRemaining > 0 && (
          <div
            className="h-full"
            style={{ width: `${remainingPct}%` }}
            aria-label={t("campaign.budget.remaining")}
          />
        )}
      </div>
      {showLegend && (
        <div className="flex items-center justify-between gap-2 mt-1.5 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-primary" />
            {t("campaign.budget.spent")}: {formatCents(spent, currency)}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500/70" />
            {t("campaign.budget.pending")}: {formatCents(rawPending, currency)}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/40" />
            {t("campaign.budget.remaining")}: {formatCents(remainingForLegend, currency)}
          </span>
        </div>
      )}
    </div>
  );
};

export default CampaignBudgetBar;
